import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getCards } from "@/services/cards";
import { QUERY_KEYS } from "@/constants/services";
import WelcomeRewardModal from "./WelcomeRewardModal";

/**
 * Decides whether to show the one-time "Account created" reward modal.
 * The modal is intentionally restricted to the Home screen only.
 */
const HOME_PATH = "/home";

const WelcomeRewardGate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((s) => s.auth.user);
  const isAuthenticated = useSelector((s) => s.auth.isAuthenticated);
  const [open, setOpen] = useState(false);

  const { data: allCards, isLoading: isLoadingCards } = useQuery({
    queryKey: [QUERY_KEYS.CARDS, user?.id],
    queryFn: getCards,
    // No Rain id → the user has no cards yet; skip the call (and the modal will
    // still show, since hasCards stays false).
    enabled: Boolean(isAuthenticated && user?.rainUserId),
  });
  const hasCards = Array.isArray(allCards) && allCards.length > 0;
  const onHome = location.pathname === HOME_PATH;

  useEffect(() => {
    if (!isAuthenticated || !user?.id || !onHome) {
      if (open) setOpen(false);
      return;
    }
    if (isLoadingCards || hasCards) return;
    const seenKey = `welcomeRewardSeen_${user.id}`;
    if (localStorage.getItem(seenKey)) return;
    // Limit to freshly-registered users (the 30-day reward window). If the
    // backend didn't send created_at, don't block on it.
    const createdAt = user.created_at || user.createdAt;
    if (createdAt) {
      const days = (Date.now() - new Date(createdAt).getTime()) / 86400000;
      if (Number.isNaN(days) || days > 30) return;
    }
    setOpen(true);
  }, [
    isAuthenticated,
    user?.id,
    user?.created_at,
    user?.createdAt,
    onHome,
    isLoadingCards,
    hasCards,
    open,
  ]);

  const dismiss = () => {
    if (user?.id) localStorage.setItem(`welcomeRewardSeen_${user.id}`, "1");
    setOpen(false);
  };
  const apply = () => {
    dismiss();
    navigate("/card");
  };

  return <WelcomeRewardModal open={open} onClose={dismiss} onApply={apply} />;
};

export default WelcomeRewardGate;
